import { Spinner } from '@/components/glitch-ui/spinner';
import { createFileRoute, useNavigate, useLocation } from '@tanstack/react-router'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { Card } from '@/components/ui/card'
import { TabCardPanel } from '@/components/glitch-ui/tab-card-panel'
import { Check, ChevronDown, Download, ChartColumnIncreasing, Info, ExternalLink } from 'lucide-react'

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
// Note: DropdownMenu imports kept for campaign selector
import { Button } from '@/components/ui/button'
import { useAtomValue } from 'jotai'
import { useQuery } from '@tanstack/react-query'
import { campaignsDataAtom, selectedPerformanceTabAtom } from '@/store'
import { useTabPersistence } from '@/hooks/useTabPersistence'
import { withOrgReset } from '@/components/glitch-ui/with-org-reset'
import { fetchPerformanceReport } from '@/lib/apis'
import { PageErrorState } from '@/components/glitch-ui/empty-state'
import { campaignQueryOptions } from '@/lib/query-options'
import { TrafficPerformanceChart } from '@/components/campaign/overview/traffic-performance-chart'
import { CostPerformanceChart } from '@/components/campaign/overview/cost-performance-chart'
import { AdvancedPerformanceChart } from '@/components/campaign/overview/advanced-performance-chart'
import { BiddingStrategyPerformance } from '@/components/campaign/bidding-strategy/bidding-strategy-performance'
import { AdPerformanceReport } from '@/components/campaign/ad-performance/ad-performance-report'
import { usePostHog } from 'posthog-js/react';
import { PerformanceData } from '@/lib/types'
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol'
import { useTestMode } from '@/hooks/useTestMode'

// Create route for performance
export const Route = createFileRoute('/_authenticated/_layout/performance/')({
  component: withOrgReset(Performance),
  staticData: {
    title: 'Performance',
    icon: ChartColumnIncreasing,
  },
})

// Tab configuration
const TAB_IDS = ['overview', 'traffic', 'cost', 'looker'] as const;
type TabId = typeof TAB_IDS[number];

// Base tabs (overview is test mode only)
const baseTabs = [
  { id: 'overview' as const, label: 'Overview', testModeOnly: true },
  { id: 'traffic' as const, label: 'Traffic', testModeOnly: false },
  { id: 'cost' as const, label: 'Cost', testModeOnly: false },
  { id: 'looker' as const, label: 'Looker', testModeOnly: false },
];

// Define time range options
type TimeRange = 'daily' | 'weekly'
type DateRange = '14d' | '12w'

export function Performance() {
  const navigate = useNavigate();
  const location = useLocation();
  const posthog = usePostHog();
  const campaigns = useAtomValue(campaignsDataAtom) // get all campaigns.
  const [chartControls, setChartControls] = useState<React.ReactNode>(null);
  const isTestMode = useTestMode();

  // Filter tabs based on test mode
  const tabs = baseTabs.filter(tab => !tab.testModeOnly || isTestMode);

  const defaultTab = isTestMode ? 'overview' : 'traffic';
  const availableTabIds = tabs.map(t => t.id) as TabId[];
  const { activeTab, handleTabChange: _handleTabChange } = useTabPersistence(
    selectedPerformanceTabAtom,
    availableTabIds,
    defaultTab,
    (tabId, replace) => navigate({ to: '/performance', hash: tabId, replace }),
    location.hash,
  );
  const handleTabChange = (tabId: TabId) => {
    posthog?.capture('performance_tab_switched', { tab_id: tabId, previous_tab: activeTab });
    _handleTabChange(tabId);
  };

  // What campaign(s) are we rendering?
  const getInitialCampaignId = () => {
    const searchParams = new URLSearchParams(window.location.search) // from search
    const campaignParam = searchParams.get('campaign')
    if (campaignParam) {
      return campaignParam
    }

    const urlPath = window.location.pathname // Otherwise check URL path
    const pathParts = urlPath.split('/')
    const lastPart = pathParts[pathParts.length - 1]

    if (lastPart && lastPart !== 'performance') {
      return lastPart   // campaign ID slug e.g. 9YlR7hdoDL
    }

    return 'all'
  }

  const [selectedCampaign, setSelectedCampaign] = useState<string>(getInitialCampaignId())
  const [performanceData, setPerformanceData] = useState<PerformanceData[]>([])
  const [previousWeekData, setPreviousWeekData] = useState<PerformanceData[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [refetchTrigger, setRefetchTrigger] = useState(0)
  const [timeRange, setTimeRange] = useState<TimeRange>('daily')
  const [dateRange, setDateRange] = useState<DateRange>('14d')

  // Campaign selector for Bidding Strategy and Ad Performance reports
  const [selectedReportCampaign, setSelectedReportCampaign] = useState<string | null>(null)

  // Set default report campaign to first campaign when campaigns load
  useEffect(() => {
    if (campaigns && campaigns.length > 0 && !selectedReportCampaign) {
      setSelectedReportCampaign(campaigns[0].slug)
    }
  }, [campaigns, selectedReportCampaign])

  // Fetch full campaign data for reports (includes googleads_campaign)
  const { data: reportCampaign } = useQuery({
    ...campaignQueryOptions(selectedReportCampaign || ''),
    enabled: !!selectedReportCampaign,
  })

  useEffect(() => {
    // Only fetch performance data if campaigns are loaded
    if (!campaigns || campaigns.length === 0) {
      setIsLoading(false); // Ensure loading is set to false if no campaigns
      return;
    }

    async function fetchPerformanceData() { // get the relevant campaign data from the server.
      setIsLoading(true) // Set loading state while fetching
      setError(null)

      try {
        let campaignId: string | number = 'all'

        // Determine the campaign ID(s) to fetch
        if (selectedCampaign === 'all') {
          // "All campaigns" option - pass comma-separated list of all campaign IDs
          if (campaigns && campaigns.length > 0) {
            // Filter out any campaigns with missing IDs
            const validCampaigns = campaigns.filter(c => c.id !== undefined && c.id !== null);

            if (validCampaigns.length > 0) {
              const allCampaignIds = validCampaigns.map(c => c.id).join(',');
              campaignId = allCampaignIds;
            } else {
              console.warn('No valid campaign IDs found');
            }
          }
        } else {
          // Single campaign selected
          const campaign = campaigns.find((c) => c.slug === selectedCampaign)
          if(campaign) {
            campaignId = campaign.id
          } else {
            throw new Error(`Campaign ID not found for campaign: ${selectedCampaign}`)
          }
        }

        const data = await fetchPerformanceReport(timeRange, dateRange, campaignId)


        setPerformanceData(data)

        if (timeRange === 'daily') {
          try {
            // Since fetchPerformanceReport already returns 14 days of data,
            // we need to split it: first 7 days for current data, days 8-14 for previous week
            const today = new Date()

            // Set yesterday as the end reference point
            const yesterday = new Date(today)
            yesterday.setDate(today.getDate() - 1)

            // One week ago from yesterday
            const oneWeekAgo = new Date(yesterday)
            oneWeekAgo.setDate(yesterday.getDate() - 7)

            // Two weeks ago from yesterday
            const twoWeeksAgo = new Date(yesterday)
            twoWeeksAgo.setDate(yesterday.getDate() - 14)

            // Current week data: between oneWeekAgo and yesterday (inclusive)
            const currentWeekData = data.filter(item => {
              const itemDate = new Date(item.date)
              return itemDate >= oneWeekAgo && itemDate <= yesterday
            })

            // Previous week data: between twoWeeksAgo and oneWeekAgo (exclusive)
            const prevWeekData = data.filter(item => {
              const itemDate = new Date(item.date)
              return itemDate >= twoWeeksAgo && itemDate < oneWeekAgo
            })


            // Update state with the filtered data
            setPerformanceData(currentWeekData)
            setPreviousWeekData(prevWeekData)
          } catch (err) {
            console.error('Error processing performance data:', err)
            // Keep the original data if there's an error
            setPreviousWeekData([])
          }
        } else {
          // For weekly view, use all data and clear previous week data
          setPreviousWeekData([])
        }
      } catch (err) {
        console.error('Error fetching performance data:', err)
        setError(
          err instanceof Error
            ? err
            : new Error('Failed to fetch performance data'),
        )
        // Reset performance data on error to prevent displaying stale data
        setPerformanceData([])
      } finally {
        setIsLoading(false) // Clear loading state when done
      }
    }

    fetchPerformanceData()
  }, [selectedCampaign, timeRange, dateRange, campaigns, refetchTrigger])



  // Shared data processing function for all charts and metrics
  const processPerformanceData = useCallback((
    rawData: PerformanceData[],
    prevWeekRawData: PerformanceData[] | undefined,
    range: 'daily' | 'weekly'
  ) => {
    if (!rawData || rawData.length === 0) {
      return {
        chartData: [],
        combinedChartData: [],
        filteredData: [],
        metrics: {
          totalClicks: 0,
          totalImpressions: 0,
          totalCost: 0,
          totalConversions: 0,
          totalAllConversions: 0,
          totalAllConversionsValue: 0,
          totalViewThroughConversions: 0,
          ctr: 0,
          conversionRate: 0,
          cpa: 0,
          avgCpc: 0
        }
      };
    }

    // Filter data based on timeRange to match chart view
    let filteredData = [...rawData];

    // For weekly view, filter to match chart display (exclude current incomplete week)
    if (range === 'weekly') {
      const today = new Date();
      // If today is Sunday (0), use previous Sunday (go back 7 days). Otherwise, last Sunday
      const currentDayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ...
      const daysToLastSunday = currentDayOfWeek === 0 ? 7 : currentDayOfWeek;

      // Set cutoff to the last Sunday
      const lastSunday = new Date(today);
      lastSunday.setDate(today.getDate() - daysToLastSunday);
      lastSunday.setHours(0, 0, 0, 0);

      // Only include data before the last Sunday for weekly view
      filteredData = rawData.filter(item => {
        const itemDate = new Date(item.date);
        return itemDate < lastSunday;
      });
    }

    // Calculate all metrics in one place
    // First, get the sum totals from filtered data
    const totalClicks = filteredData.reduce((sum, item) => sum + (Number(item.clicks) || 0), 0);
    const totalImpressions = filteredData.reduce((sum, item) => sum + (Number(item.impressions) || 0), 0);
    const totalCost = filteredData.reduce((sum, item) => sum + (Number(item.cost) || 0), 0);
    const totalConversions = filteredData.reduce((sum, item) => sum + (Number(item.conversions) || 0), 0);
    const totalAllConversions = filteredData.reduce((sum, item) => sum + (Number(item.all_conversions) || 0), 0);
    const totalAllConversionsValue = filteredData.reduce((sum, item) => sum + (Number(item.all_conversions_value) || 0), 0);
    const totalViewThroughConversions = filteredData.reduce((sum, item) => sum + (Number(item.view_through_conversions) || 0), 0);

    // Then, calculate all derived metrics
    const ctr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
    const conversionRate = totalClicks > 0 ? (totalConversions / totalClicks) * 100 : 0;
    const cpa = totalConversions > 0 ? totalCost / totalConversions : 0;
    const avgCpc = totalClicks > 0 ? totalCost / totalClicks : 0;

    // Calculate Correct Start and End Date for the Graphs.
    const today = new Date();
    const endDate = new Date(today);
    endDate.setDate(today.getDate() - 1);

    const startDate = new Date(endDate);

    if (range === 'daily') {
      startDate.setDate(endDate.getDate() - 6);
    } else {
      // First find the most recent Sunday
      const currentDayOfWeek = today.getDay();
      const daysToLastSunday = currentDayOfWeek === 0 ? 7 : currentDayOfWeek;

      const lastSunday = new Date(today);
      lastSunday.setDate(today.getDate() - daysToLastSunday);

      // Set end date to that Sunday
      endDate.setTime(lastSunday.getTime());

      // Start date should be 14 weeks before end date, adjusted to Monday
      startDate.setTime(endDate.getTime());
      startDate.setDate(startDate.getDate() - (13 * 7) - 6);
    }

    // Initialise a 0 array with all dates in the range
    const allDates: PerformanceData[] = [];
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      allDates.push({
        date: currentDate.toISOString().split('T')[0],
        clicks: 0,
        impressions: 0,
        conversions: 0,
        cost: 0,
        ctr: 0,
        avgCpc: 0,
        cpa: 0
      });

      if (range === 'daily') {
        currentDate.setDate(currentDate.getDate() + 1);
      } else {
        currentDate.setDate(currentDate.getDate() + 7);
      }
    }

    // Map actual data onto the complete date range
    if (rawData.length > 0) {
      const dataMap = new Map();
      rawData.forEach(item => {
        const dateKey = item.date.toString().split('T')[0];
        dataMap.set(dateKey, item);
      });

      for (let i = 0; i < allDates.length; i++) {
        const dateKey = allDates[i].date;
        if (dataMap.has(dateKey)) {
          allDates[i] = {
            ...allDates[i],
            ...dataMap.get(dateKey)
          };
        }
      }
    }

    // Sort by date (oldest to newest) and calculate all derived metrics consistently
    const chartData = allDates
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .map(item => {
        // Calculate all derived metrics for each data point
        return {
          ...item,
          ctr: item.impressions > 0 ? (item.clicks / item.impressions) * 100 : 0,
          avgCpc: item.clicks > 0 ? item.cost / item.clicks : 0,
          cpa: item.conversions && item.conversions > 0 ? item.cost / item.conversions : 0
        };
      });

    // Process previous week data for both charts (for daily view only)
    let combinedChartData = chartData;

    if (range === 'daily' && prevWeekRawData && prevWeekRawData.length > 0) {
      // Map current dates to previous week dates
      const previousWeekMap = new Map();
      prevWeekRawData.forEach(item => {
        const date = new Date(item.date);
        // Create key based on day of week (0-6)
        const dayOfWeek = date.getDay();

        // Calculate all derived metrics consistently
        const derivedMetrics = {
          ctr: item.impressions > 0 ? (item.clicks / item.impressions) * 100 : 0,
          avgCpc: item.clicks > 0 ? item.cost / item.clicks : 0,
          cpa: item.conversions && item.conversions > 0
            ? item.cost / item.conversions
            : 0
        };

        previousWeekMap.set(dayOfWeek, {
          ...item,
          ...derivedMetrics
        });
      });

      // Combine current data with previous week data
      combinedChartData = chartData.map(currentItem => {
        const currentDate = new Date(currentItem.date);
        const dayOfWeek = currentDate.getDay();
        const prevWeekItem = previousWeekMap.get(dayOfWeek);

        return {
          ...currentItem,
          // For cost performance chart
          prevWeekCost: prevWeekItem?.cost || null,
          prevWeekAvgCpc: prevWeekItem?.avgCpc || null,
          prevWeekCpa: prevWeekItem?.cpa || null,
          // For traffic performance chart
          prevWeekImpressions: prevWeekItem?.impressions || null,
          prevWeekClicks: prevWeekItem?.clicks || null,
          prevWeekConversions: prevWeekItem?.conversions || null,
        };
      });
    }

    return {
      chartData,
      combinedChartData,
      filteredData,
      metrics: {
        totalClicks,
        totalImpressions,
        totalCost,
        totalConversions,
        totalAllConversions,
        totalAllConversionsValue,
        totalViewThroughConversions,
        ctr,
        conversionRate,
        cpa,
        avgCpc
      }
    };
  }, []);

  // Apply the shared data processing function
  const { chartData, combinedChartData, metrics } = useMemo(() => {
    return processPerformanceData(performanceData, previousWeekData, timeRange);
  }, [performanceData, previousWeekData, timeRange, processPerformanceData]);

  // Destructure metrics for ease of use
  const { totalClicks, totalImpressions, totalCost: totalSpend, totalConversions, totalAllConversions, totalAllConversionsValue, totalViewThroughConversions, ctr, conversionRate, cpa, avgCpc } = metrics;

  const { symbol: _cs } = useCurrencySymbol();
  const currencySymbol = _cs ?? '€';

  const exportToCSV = () => {
    posthog?.capture('performance_csv_exported');

    const headers = ["Date", "Clicks", "Impressions", "Cost", "Conversions", "All Conversions", "All Conversions Value", "View-Through Conversions", "CTR", "Cost/Click", "Cost/Conv"]; //  CSV header row
    let csvRows = [headers.join(',')];

    // Use the processed chart data that has all metrics properly calculated
    if (chartData && chartData.length > 0) {
      const dataRows = chartData.map(row => {
        // Format date as MM/DD/YYYY
        const date = new Date(row.date);
        const formattedDate = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;

        return [
          formattedDate,
          row.clicks,
          row.impressions,
          row.cost.toFixed(2),
          (row.conversions || 0).toFixed(2),
          (row.all_conversions || 0).toFixed(2),
          (row.all_conversions_value || 0).toFixed(2),
          (row.view_through_conversions || 0).toFixed(2),
          row.ctr.toFixed(2) + '%',
          currencySymbol + row.avgCpc.toFixed(2),
          currencySymbol + row.cpa.toFixed(2)
        ].join(',');
      });

      csvRows = [...csvRows, ...dataRows];
    }

    // Join rows with newlines to create the CSV content
    const csvContent = csvRows.join('\n');

    // Create a Blob containing the CSV data
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    // Create a URL for the Blob
    const url = URL.createObjectURL(blob);

    // Create a temporary link element to trigger the download
    const link = document.createElement('a');
    link.href = url;

    // Create a filename with campaign name and date range
    const campaignName = selectedCampaign === 'all'
      ? 'All-Campaigns'
      : campaigns.find(c => c.slug === selectedCampaign)?.name.replace(/\s+/g, '-') || 'Campaign';

    const timeRangeText = timeRange === 'daily' ? 'Daily' : 'Weekly';
    const today = new Date().toISOString().split('T')[0];
    link.download = `${campaignName}-${timeRangeText}-Performance-${today}.csv`;

    // Append the link to the document, click it, and remove it
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Release the URL object
    URL.revokeObjectURL(url);
  };

  // Show error state if the primary performance data failed to load (4xx/5xx)
  if (error) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between py-0.5">
          <div className="flex items-center gap-3">
          </div>
        </div>
        <div className="flex-1">
          <PageErrorState
            title="Couldn't Load Performance"
            description="This page failed to load. This is a loading error, not missing data, so nothing has been lost. Retry, or contact support if it persists."
            onRetry={() => setRefetchTrigger((n) => n + 1)}
          />
        </div>
      </div>
    );
  }

  // Show empty state if no campaigns
  if (!campaigns || campaigns.length === 0) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between py-0.5">
          <div className="flex items-center gap-3">
          </div>
        </div>
        <div className="flex-1 space-y-6">
          <TabCardPanel
            tabs={tabs.map((tab) => ({
              id: tab.id,
              label: tab.label,
              isActive: activeTab === tab.id,
              onClick: () => handleTabChange(tab.id),
            }))}
          >
            <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
              <ChartColumnIncreasing className="h-12 w-12 text-gray-500 mb-4" strokeWidth={1} />
              <h3 className="text-lg font-semibold text-gray-500 mb-2">No Performance Data Yet</h3>
              <p className="text-gray-500">Performance data will be available once your campaigns are live and running for a few days</p>
            </div>
          </TabCardPanel>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between py-0.5">
        <div className="flex items-center gap-3">
        </div>
      </div>

      <div className="flex-1 space-y-6">
        <div>
          <TabCardPanel
            tabs={tabs.map((tab) => ({
              id: tab.id,
              label: tab.label,
              isActive: activeTab === tab.id,
              onClick: () => handleTabChange(tab.id),
            }))}
            actions={
              <div className="flex items-center gap-2">
                {/* Chart controls from AdvancedPerformanceChart */}
                {activeTab === 'overview' && chartControls}

                {/* Campaign Selector */}
                {campaigns && campaigns.length > 1 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="h-9 w-[200px] justify-between gap-2">
                        <span className="truncate">
                          {selectedCampaign === 'all'
                            ? 'All Campaigns'
                            : campaigns.find((c) => c.slug === selectedCampaign)?.name?.split(' | ').pop() || 'Select Campaign'}
                        </span>
                        <ChevronDown className="h-4 w-4 shrink-0" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[200px] max-h-80 overflow-y-auto">
                      <DropdownMenuItem
                        onClick={() => setSelectedCampaign('all')}
                        className="flex items-center justify-between gap-2"
                      >
                        <span className={selectedCampaign === 'all' ? 'font-medium' : ''}>All Campaigns</span>
                        {selectedCampaign === 'all' && <Check className="h-4 w-4" />}
                      </DropdownMenuItem>
                      {campaigns.map((campaign) => (
                        <DropdownMenuItem
                          key={campaign.slug}
                          onClick={() => setSelectedCampaign(campaign.slug)}
                          className="flex items-center justify-between gap-2"
                        >
                          <span className={selectedCampaign === campaign.slug ? 'font-medium' : ''}>
                            {campaign.name.split(' | ').pop() || campaign.name}
                          </span>
                          {selectedCampaign === campaign.slug && <Check className="h-4 w-4" />}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {/* Daily/Weekly Toggle */}
                <div className="flex items-center rounded-md border border-gray-200 bg-white overflow-hidden h-9">
                  <button
                    onClick={() => { posthog?.capture('performance_date_range_changed', { time_range: 'daily' }); setTimeRange('daily'); setDateRange('14d'); }}
                    className={`px-3 h-full text-sm font-medium transition-colors ${
                      timeRange === 'daily'
                        ? 'bg-gray-200 text-gray-900'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Daily
                  </button>
                  <button
                    onClick={() => { posthog?.capture('performance_date_range_changed', { time_range: 'weekly' }); setTimeRange('weekly'); setDateRange('12w'); }}
                    className={`px-3 h-full text-sm font-medium transition-colors ${
                      timeRange === 'weekly'
                        ? 'bg-gray-200 text-gray-900'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Weekly
                  </button>
                </div>

                {/* Export Button */}
                <Button
                  variant="outline"
                  className="h-9 text-gray-900"
                  disabled={isLoading}
                  onClick={() => exportToCSV()}
                >
                  {isLoading ? (
                    <Spinner size="sm" className="mr-2" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Export
                </Button>
              </div>
            }
          >
              {/* Overview Tab */}
              {activeTab === 'overview' && !isLoading && performanceData.length === 0 && (
                <div className="border rounded-lg bg-white overflow-hidden">
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <ChartColumnIncreasing className="h-12 w-12 text-gray-500 mb-4" strokeWidth={1} />
                    <h3 className="text-lg font-semibold text-gray-500 mb-2">No Performance Data Yet</h3>
                    <p className="text-gray-500">Performance data will be available once your campaigns are live and running for a few days</p>
                  </div>
                </div>
              )}
              {activeTab === 'overview' && (isLoading || performanceData.length > 0) && (
                <div className="space-y-8">
                  {/* Advanced Performance Chart - Configurable chart at top */}
                  <AdvancedPerformanceChart
                    currencySymbol={currencySymbol}
                    onControlsReady={setChartControls}
                  />

                  <div>
                    <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                      {/* First row: Impressions, Clicks, Conversions, Cost, Cost/Click, Cost/Conv */}
                      <Card className="bg-white p-4 border-0 shadow-none bg-gray-50">
                        <div className="flex justify-between items-start">
                          <h3 className="text-sm font-medium text-gray-700">
                            Impressions
                          </h3>
                        </div>
                        {isLoading ? (
                          <div className="h-8 flex items-center justify-center mt-2">
                            <div className="animate-pulse bg-gray-200 h-6 w-20 rounded"></div>
                          </div>
                        ) : (
                          <p className="text-2xl font-semibold mt-2 text-center">
                            {totalImpressions.toLocaleString()}
                          </p>
                        )}
                      </Card>

                      <Card className="bg-white p-4 border-0 shadow-none bg-gray-50">
                        <div className="flex justify-between items-start">
                          <h3 className="text-sm font-medium text-gray-700">Clicks</h3>
                        </div>
                        {isLoading ? (
                          <div className="h-8 flex items-center justify-center mt-2">
                            <div className="animate-pulse bg-gray-200 h-6 w-16 rounded"></div>
                          </div>
                        ) : (
                          <>
                            <p className="text-2xl font-semibold mt-2 text-center">
                              {totalClicks.toLocaleString()}
                            </p>
                            <p className="text-xs text-gray-500 mt-1 text-center">
                              CTR: {ctr.toFixed(2)}%
                            </p>
                          </>
                        )}
                      </Card>

                      <Card className="bg-white p-4 border-0 shadow-none bg-gray-50">
                        <div className="flex justify-between items-start">
                          <h3 className="text-sm font-medium text-gray-700">
                            Conversions
                          </h3>
                        </div>
                        {isLoading ? (
                          <div className="h-8 flex items-center justify-center mt-2">
                            <div className="animate-pulse bg-gray-200 h-6 w-16 rounded"></div>
                          </div>
                        ) : (
                          <>
                            <p className="text-2xl font-semibold mt-2 text-center">
                              {Math.floor(totalConversions)}
                            </p>
                            <p className="text-xs text-gray-500 mt-1 text-center">
                              Conv Rate: {conversionRate.toFixed(2)}%
                            </p>
                          </>
                        )}
                      </Card>

                      {/* Only show All Conversions card if it's different from Conversions */}
                      {totalAllConversions !== totalConversions && (
                        <Card className="bg-white p-4 border-0 shadow-none bg-gray-50">
                          <div className="flex justify-between items-start">
                            <h3 className="text-sm font-medium text-gray-700">
                              All Conversions
                            </h3>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="h-4 w-4 text-gray-400 cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <p className="text-xs mb-1 font-semibold">All Conversions includes:</p>
                                  <ul className="text-xs list-disc list-inside space-y-1">
                                    <li>Last-click conversions</li>
                                    <li>View-through conversions</li>
                                    <li>Cross-device conversions</li>
                                    <li>Store visits, calls, etc.</li>
                                  </ul>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          {isLoading ? (
                            <div className="h-8 flex items-center justify-center mt-2">
                              <div className="animate-pulse bg-gray-200 h-6 w-16 rounded"></div>
                            </div>
                          ) : (
                            <>
                              <p className="text-2xl font-semibold mt-2 text-center">
                                {Math.floor(totalAllConversions)}
                              </p>
                              {totalAllConversionsValue > 0 && (
                                <p className="text-xs text-gray-500 mt-1 text-center">
                                  Value: {currencySymbol}{totalAllConversionsValue.toFixed(2)}
                                </p>
                              )}
                              {totalViewThroughConversions > 0 && (
                                <p className="text-xs text-gray-400 mt-1 text-center">
                                  View-through: {Math.floor(totalViewThroughConversions)}
                                </p>
                              )}
                            </>
                          )}
                        </Card>
                      )}

                      <Card className="bg-white p-4 border-0 shadow-none bg-gray-50">
                        <div className="flex justify-between items-start">
                          <h3 className="text-sm font-medium text-gray-700">Cost</h3>
                        </div>
                        {isLoading ? (
                          <div className="h-8 flex items-center justify-center mt-2">
                            <div className="animate-pulse bg-gray-200 h-6 w-20 rounded"></div>
                          </div>
                        ) : (
                          <p className="text-2xl font-semibold mt-2 text-center">
                            {currencySymbol}{totalSpend.toFixed(2)}
                          </p>
                        )}
                      </Card>

                      <Card className="bg-white p-4 border-0 shadow-none bg-gray-50">
                        <div className="flex justify-between items-start">
                          <h3 className="text-sm font-medium text-gray-700">
                            Cost/Click
                          </h3>
                        </div>
                        {isLoading ? (
                          <div className="h-8 flex items-center justify-center mt-2">
                            <div className="animate-pulse bg-gray-200 h-6 w-20 rounded"></div>
                          </div>
                        ) : (
                          <p className="text-2xl font-semibold mt-2 text-center">
                            {currencySymbol}{avgCpc.toFixed(2)}
                          </p>
                        )}
                      </Card>

                      <Card className="bg-white p-4 border-0 shadow-none bg-gray-50">
                        <div className="flex justify-between items-start">
                          <h3 className="text-sm font-medium text-gray-700">Cost/Conv</h3>
                        </div>
                        {isLoading ? (
                          <div className="h-8 flex items-center justify-center mt-2">
                            <div className="animate-pulse bg-gray-200 h-6 w-20 rounded"></div>
                          </div>
                        ) : (
                          <p className="text-2xl font-semibold mt-2 text-center">{currencySymbol}{cpa.toFixed(2)}</p>
                        )}
                      </Card>
                    </div>
                  </div>

                  {/* Campaign Reports Section */}
                  {campaigns && campaigns.length > 0 && (
                    <div className="space-y-6 pt-4 border-t border-gray-200">
                      {/* Campaign Selector */}
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-gray-700">Campaign:</span>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" className="h-9 gap-2">
                              {reportCampaign?.name
                                ? reportCampaign.name.split(' | ').pop() || reportCampaign.name
                                : 'Select campaign'}
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto">
                            {campaigns.map((campaign) => (
                              <DropdownMenuItem
                                key={campaign.slug}
                                onClick={() => setSelectedReportCampaign(campaign.slug)}
                                className="flex items-center gap-2"
                              >
                                {selectedReportCampaign === campaign.slug && (
                                  <Check className="h-4 w-4" />
                                )}
                                <span className={selectedReportCampaign === campaign.slug ? 'font-medium' : ''}>
                                  {campaign.name.split(' | ').pop() || campaign.name}
                                </span>
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      {/* Bidding Strategy Report */}
                      {reportCampaign && (
                        <BiddingStrategyPerformance
                          dateRange="LAST_14_DAYS"
                          campaignId={reportCampaign.id}
                        />
                      )}

                      {/* Ad Performance Report */}
                      {reportCampaign?.googleads_campaign?.googleads_campaign_id && (
                        <AdPerformanceReport
                          campaignId={reportCampaign.googleads_campaign.googleads_campaign_id}
                          dateRange="LAST_30_DAYS"
                        />
                      )}
                    </div>
                  )}

                </div>
              )}

              {/* Traffic Tab */}
              {activeTab === 'traffic' && (
                <div className="space-y-6">
                  <TrafficPerformanceChart
                    key={`traffic-chart-${timeRange}`}
                    data={chartData}
                    combinedChartData={combinedChartData}
                    isLoading={isLoading}
                    error={error}
                    timeRange={timeRange}
                    campaignName={
                      selectedCampaign === 'all'
                        ? undefined
                        : campaigns.find((c) => c.slug === selectedCampaign)?.name
                    }
                    currencySymbol={currencySymbol}
                  />

                  {/* Traffic Metrics - below the chart */}
                  <div className="grid grid-cols-3 gap-4">
                    <Card className="bg-white p-4 border-0 shadow-none bg-gray-50">
                      <div className="flex justify-between items-start">
                        <h3 className="text-sm font-medium text-gray-700">Impressions</h3>
                      </div>
                      {isLoading ? (
                        <div className="h-8 flex items-center justify-center mt-2">
                          <div className="animate-pulse bg-gray-200 h-6 w-20 rounded"></div>
                        </div>
                      ) : (
                        <p className="text-2xl font-semibold mt-2 text-center">
                          {totalImpressions.toLocaleString()}
                        </p>
                      )}
                    </Card>

                    <Card className="bg-white p-4 border-0 shadow-none bg-gray-50">
                      <div className="flex justify-between items-start">
                        <h3 className="text-sm font-medium text-gray-700">Clicks</h3>
                      </div>
                      {isLoading ? (
                        <div className="h-8 flex items-center justify-center mt-2">
                          <div className="animate-pulse bg-gray-200 h-6 w-16 rounded"></div>
                        </div>
                      ) : (
                        <>
                          <p className="text-2xl font-semibold mt-2 text-center">
                            {totalClicks.toLocaleString()}
                          </p>
                          <p className="text-xs text-gray-500 mt-1 text-center">
                            CTR: {ctr.toFixed(2)}%
                          </p>
                        </>
                      )}
                    </Card>

                    <Card className="bg-white p-4 border-0 shadow-none bg-gray-50">
                      <div className="flex justify-between items-start">
                        <h3 className="text-sm font-medium text-gray-700">Conversions</h3>
                      </div>
                      {isLoading ? (
                        <div className="h-8 flex items-center justify-center mt-2">
                          <div className="animate-pulse bg-gray-200 h-6 w-16 rounded"></div>
                        </div>
                      ) : (
                        <>
                          <p className="text-2xl font-semibold mt-2 text-center">
                            {Math.floor(totalConversions)}
                          </p>
                          <p className="text-xs text-gray-500 mt-1 text-center">
                            Conv Rate: {conversionRate.toFixed(2)}%
                          </p>
                        </>
                      )}
                    </Card>
                  </div>

                  {/* Campaign Reports Section - moved from Overview */}
                  {campaigns && campaigns.length > 0 && (
                    <div className="space-y-6 pt-4 border-t border-gray-200">
                      {/* Campaign Selector */}
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-gray-700">Campaign:</span>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" className="h-9 gap-2">
                              {reportCampaign?.name
                                ? reportCampaign.name.split(' | ').pop() || reportCampaign.name
                                : 'Select campaign'}
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto">
                            {campaigns.map((campaign) => (
                              <DropdownMenuItem
                                key={campaign.slug}
                                onClick={() => setSelectedReportCampaign(campaign.slug)}
                                className="flex items-center gap-2"
                              >
                                {selectedReportCampaign === campaign.slug && (
                                  <Check className="h-4 w-4" />
                                )}
                                <span className={selectedReportCampaign === campaign.slug ? 'font-medium' : ''}>
                                  {campaign.name.split(' | ').pop() || campaign.name}
                                </span>
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      {/* Bidding Strategy Report */}
                      {reportCampaign && (
                        <BiddingStrategyPerformance
                          dateRange="LAST_14_DAYS"
                          campaignId={reportCampaign.id}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Cost Tab */}
              {activeTab === 'cost' && (
                <div className="space-y-6">
                  <CostPerformanceChart
                    key={`cost-chart-${timeRange}`}
                    data={chartData}
                    combinedChartData={combinedChartData}
                    isLoading={isLoading}
                    error={error}
                    timeRange={timeRange}
                    campaignName={
                      selectedCampaign === 'all'
                        ? undefined
                        : campaigns.find((c) => c.slug === selectedCampaign)?.name
                    }
                    currencySymbol={currencySymbol}
                  />

                  {/* Cost Metrics - below the chart */}
                  <div className="grid grid-cols-3 gap-4">
                    <Card className="bg-white p-4 border-0 shadow-none bg-gray-50">
                      <div className="flex justify-between items-start">
                        <h3 className="text-sm font-medium text-gray-700">Cost</h3>
                      </div>
                      {isLoading ? (
                        <div className="h-8 flex items-center justify-center mt-2">
                          <div className="animate-pulse bg-gray-200 h-6 w-20 rounded"></div>
                        </div>
                      ) : (
                        <p className="text-2xl font-semibold mt-2 text-center">
                          {currencySymbol}{totalSpend.toFixed(2)}
                        </p>
                      )}
                    </Card>

                    <Card className="bg-white p-4 border-0 shadow-none bg-gray-50">
                      <div className="flex justify-between items-start">
                        <h3 className="text-sm font-medium text-gray-700">Cost/Click</h3>
                      </div>
                      {isLoading ? (
                        <div className="h-8 flex items-center justify-center mt-2">
                          <div className="animate-pulse bg-gray-200 h-6 w-20 rounded"></div>
                        </div>
                      ) : (
                        <p className="text-2xl font-semibold mt-2 text-center">
                          {currencySymbol}{avgCpc.toFixed(2)}
                        </p>
                      )}
                    </Card>

                    <Card className="bg-white p-4 border-0 shadow-none bg-gray-50">
                      <div className="flex justify-between items-start">
                        <h3 className="text-sm font-medium text-gray-700">Cost/Conv</h3>
                      </div>
                      {isLoading ? (
                        <div className="h-8 flex items-center justify-center mt-2">
                          <div className="animate-pulse bg-gray-200 h-6 w-20 rounded"></div>
                        </div>
                      ) : (
                        <p className="text-2xl font-semibold mt-2 text-center">
                          {currencySymbol}{cpa.toFixed(2)}
                        </p>
                      )}
                    </Card>
                  </div>
                </div>
              )}

              {/* Looker Tab */}
              {activeTab === 'looker' && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <img src="/looker-studio.svg" className="h-12 w-12 mb-4" alt="Looker Studio" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Looker Studio</h3>
                  <p className="text-gray-500 max-w-md mb-6">
                    Looker Studio is Google's free business intelligence tool that connects to your Google Ads data for advanced reporting and visualizations.
                  </p>
                  <Button
                    className="bg-[#290BD9] hover:bg-[#2309C5]"
                    onClick={() => {
                      window.open(
                        'https://lookerstudio.google.com/u/0/reporting/323a553e-802f-429f-927e-c335e44d6f75/preview/',
                        '_blank'
                      );
                    }}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open Looker Studio
                  </Button>
                  <p className="text-xs text-gray-400 mt-3">Opens in a new window</p>
                </div>
              )}
          </TabCardPanel>
        </div>
      </div>
    </div>
  )
}
